import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import BackToTop from "@/components/feature/BackToTop";
import { categories, comics } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id = "" } = useParams();
  const comic = comics.find((c) => c.id === id) ?? comics[0];
  const related = comics.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.chapters, 12) }, (_, i) => comic.chapters - i);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      {/* 面包屑 */}
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
          <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">
            {comic.category}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-950">{comic.title}</span>
        </nav>
      </div>

      {/* 详情头 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-6 pb-10">
        <div className="flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="mx-auto w-48 shrink-0 md:mx-0 md:w-60">
            <img
              src={comic.cover}
              alt={`${comic.title} 韩国妖精漫画封面`}
              title={`${comic.title} 妖精漫画`}
              className="aspect-[3/4] w-full rounded-lg object-cover object-top"
            />
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {comic.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  #{t}
                </span>
              ))}
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  comic.status === "已完结" ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-700"
                }`}
              >
                {comic.status}
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black md:text-4xl">{comic.title}</h1>
            <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">{comic.intro}</p>

            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-foreground-500">作者</dt>
                <dd className="mt-0.5 font-medium text-foreground-950">{comic.author}</dd>
              </div>
              <div>
                <dt className="text-foreground-500">评分</dt>
                <dd className="mt-0.5 font-medium text-primary-500">
                  <i className="ri-star-fill"></i> {comic.rating.toFixed(1)} / 5
                </dd>
              </div>
              <div>
                <dt className="text-foreground-500">话数</dt>
                <dd className="mt-0.5 font-medium text-foreground-950">{comic.chapters} 话</dd>
              </div>
              <div>
                <dt className="text-foreground-500">分类</dt>
                <dd className="mt-0.5 font-medium text-accent-700">{comic.category}</dd>
              </div>
              <div>
                <dt className="text-foreground-500">最近更新</dt>
                <dd className="mt-0.5 font-medium text-foreground-950">{comic.update}</dd>
              </div>
            </dl>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-play-fill"></i>
                继续阅读
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-6 py-3 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-500 transition-colors"
              >
                <i className="ri-bookmark-line"></i>
                收藏追更
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 世界观设定 + 章节列表 */}
      <section className="border-y border-background-200/60 bg-background-100/50">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 md:px-6 py-10 md:grid-cols-2 md:py-14">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold md:text-2xl">
              <i className="ri-sparkling-2-fill text-primary-500"></i>
              世界观设定
            </h2>
            <ul className="mt-5 space-y-3">
              {comic.worldview.map((w) => (
                <li key={w} className="flex items-start gap-3 text-sm leading-6 text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-1 text-accent-500"></i>
                  {w}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm leading-7 text-foreground-600">{comic.description}</p>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold md:text-2xl">
              <i className="ri-book-open-fill text-primary-500"></i>
              最新章节
            </h2>
            <ul className="mt-5 grid grid-cols-1 gap-2">
              {chapters.map((ch) => (
                <li key={ch}>
                  <Link
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between rounded-md border border-background-200/60 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <span>第 {ch} 话</span>
                    <span className="text-xs text-foreground-500">2026 更新</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-medium text-primary-500">RELATED</p>
            <h2 className="mt-2 font-heading text-2xl font-bold">同类推荐</h2>
          </div>
          <Link
            to={`/category/${comic.categorySlug}`}
            className="hidden items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 sm:inline-flex"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {related.length > 0 ? (
            related.map((c) => <ComicCard key={c.id} comic={c} />)
          ) : (
            <p className="col-span-full py-10 text-center text-sm text-foreground-600">
              暂无同类作品，看看其他分类吧
            </p>
          )}
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}